import { cn } from "@win-reality/react-ui";

/** Key that selects an option (or triggers a button) while it's live. */
export const HotkeyBadge = ({
  hotkey,
  isDisabled = false,
  className,
}: {
  hotkey: string;
  isDisabled?: boolean;
  className?: string;
}) => (
  <span
    className={cn(
      // Size
      "h-5 min-w-5 shrink-0",
      // Layout
      "flex items-center justify-center",
      // Spacing
      "px-1",
      // Border
      "rounded",
      // Background
      "bg-tagging-hotkey",
      // Text
      "text-[10px] font-semibold normal-case",
      isDisabled
        ? "text-tagging-option-disabled-text"
        : "text-gray-700 dark:text-gray-300",
      className,
    )}
  >
    {hotkey}
  </span>
);

type OptionButtonProps = {
  label: string;
  hotkey?: string;
  isSelected?: boolean;
  isDisabled?: boolean;
  /** The tracker suggested this option (for tests; not shown) */
  isSuggested?: boolean;
  onClick: () => void;
  /** Test hooks: literal id and the option's API enum string */
  "data-testid"?: string;
  "data-value"?: string;
};

export const OptionButton = ({
  label,
  hotkey,
  isSelected = false,
  isDisabled = false,
  isSuggested = false,
  onClick,
  "data-testid": testId,
  "data-value": dataValue,
}: OptionButtonProps) => (
  <button
    type="button"
    data-testid={testId}
    data-value={dataValue}
    data-suggested={isSuggested || undefined}
    aria-pressed={isSelected}
    disabled={isDisabled}
    onClick={onClick}
    className={cn(
      // Size
      "min-h-10 w-full",
      // Layout
      "flex items-center justify-between gap-2",
      // Spacing
      "px-3 py-1.5",
      // Border
      "rounded-[5px] border",
      // Text
      "text-left text-sm font-semibold uppercase",
      // Effects
      "transition-colors",
      // States
      isDisabled
        ? "cursor-not-allowed border-transparent bg-tagging-option-disabled text-tagging-option-disabled-text"
        : isSelected
          ? "border-tagging-option-selected-border bg-tagging-option-selected text-gray-900 dark:text-white"
          : "border-tagging-option-border bg-tagging-option text-gray-900 hover:border-tagging-option-selected-border/50 dark:text-white",
    )}
  >
    <span className="min-w-0 truncate">{label}</span>

    {hotkey && <HotkeyBadge hotkey={hotkey} isDisabled={isDisabled} />}
  </button>
);
